import axios from 'axios';

import { ppaUrls } from 'config/urls';

import {
    CREATE_LENS_TOOLTIP_MESSAGES,
    LENS_NAME_VALIDATION_MESSAGES,
} from './customLens.constants';
import type {
    LensConfigApiCatalogGroup,
    LensConfigApiCatalogSection,
    LensConfigApiResponse,
    LensConfigApiSelectedItem,
    PPALensListResponse,
} from './customLens.api.types';
import type {
    CreateCustomLensRequest,
    CustomLensAttributePayload,
    CustomLensFeatureInput,
    CustomLensFormData,
    CustomLensFormValidation,
    CustomLensFormValidationParams,
    CustomLensParsedConfig,
    SelectionFieldConfig,
    SelectionFieldOptionMeta,
    SystemLensEditFormData,
    UpdateLensRequest,
    UpsertCustomLensResponse,
} from './customLens.types';

/** Letters, numbers, and spaces only. */
const LENS_NAME_PATTERN = /^[a-zA-Z0-9 ]*$/;

const sortByDisplayOrder = <T extends { display_order?: number }>(items: T[]) =>
    [...items].sort((left, right) => (left.display_order ?? 0) - (right.display_order ?? 0));

const toFieldKey = (input: Pick<CustomLensFeatureInput, 'input_id' | 'input_label'>): string =>
    `${input.input_id}-${input.input_label.trim().toLowerCase().replace(/\s+/g, '_')}`;

const toSelectedPanelTitle = (sectionLabel: string | undefined, fallback: string) => {
    if (!sectionLabel?.trim()) {
        return fallback;
    }

    const normalized = sectionLabel
        .replace(/^Choose\s+/i, '')
        .replace(/\s+to add$/i, '')
        .trim();

    return normalized ? `Selected ${normalized}` : fallback;
};

const mapCatalogGroupToSelectionField = (
    group: LensConfigApiCatalogGroup,
    sectionKey: string,
): SelectionFieldConfig => {
    const optionMeta: SelectionFieldOptionMeta[] = sortByDisplayOrder(group.items).map((item) => ({
        label: item.label,
        id: item.attribute_key,
        id_type: item.ui_role === 'KPI' ? 'kpi_id' : 'attribute_type',
        dimension: item.dimension ?? undefined,
        attribute_id: item.attribute_id,
        attribute_key: item.attribute_key,
        isDisabled: item.is_disabled ?? false,
    }));

    return {
        key: toFieldKey({
            input_id: group.group_key,
            input_label: group.group_name,
        }),
        label: group.group_name,
        sectionKey,
        groupKey: group.group_key,
        placeholder: group.placeholder,
        options: optionMeta.map((option) => option.label),
        optionMeta,
    };
};

const buildEmptySelections = (fields: SelectionFieldConfig[]): Record<string, string[]> =>
    Object.fromEntries(fields.map((field) => [field.key, []]));

const sortSelectedItemsByDisplayOrder = (items: LensConfigApiSelectedItem[]) =>
    [...items].sort((left, right) => (left.display_order ?? 0) - (right.display_order ?? 0));

const parseIncludeAiSuggestions = (payload: LensConfigApiResponse, selectedItems: LensConfigApiSelectedItem[]): boolean =>
    Boolean(
        payload.system_columns?.some(
            (column) => column.system_column_key === 'ai_suggestion' && column.is_selected,
        ) ??
            selectedItems.some(
                (item) => item.item_type === 'SYSTEM_COLUMN' && item.system_column_key === 'ai_suggestion',
            ),
    );

export const parseCustomLensConfigFromApi = (data: unknown): CustomLensParsedConfig | null => {
    if (data == null || typeof data !== 'object') {
        return null;
    }

    const payload = data as LensConfigApiResponse;
    const viewConfig = payload.view_config;
    if (!viewConfig) {
        return null;
    }

    const lensNameInput = viewConfig.inputs?.find((input) => input.input_id === 'lens_name');
    const aiColumn = payload.system_columns?.find((column) => column.system_column_key === 'ai_suggestion');
    const dimensionSection = sortByDisplayOrder(payload.catalog?.sections ?? []).find(
        (section) => section.section_key === 'dimensions',
    );
    const kpiSection = sortByDisplayOrder(payload.catalog?.sections ?? []).find(
        (section) => section.section_key === 'kpis',
    );

    const dimensionFields = sortByDisplayOrder(dimensionSection?.groups ?? []).map((group) =>
        mapCatalogGroupToSelectionField(group, 'dimensions'),
    );
    const kpiFields = sortByDisplayOrder(kpiSection?.groups ?? []).map((group) =>
        mapCatalogGroupToSelectionField(group, 'kpis'),
    );

    return {
        featureName: viewConfig.title ?? '',
        lensNameMaxLength: lensNameInput?.max_length ?? 20,
        lensNamePlaceholder: lensNameInput?.placeholder ?? '',
        showIncludeAiSuggestions: Boolean(aiColumn),
        includeAiSuggestionsDefault: aiColumn?.is_selected ?? false,
        includeAiSuggestionsDisabled: aiColumn?.is_disabled ?? false,
        includeAiSuggestionsLabel: aiColumn?.label ?? '',
        dimensionSectionLabel: dimensionSection?.section_label ?? '',
        kpiSectionLabel: kpiSection?.section_label ?? '',
        selectedDimensionsPanelTitle: toSelectedPanelTitle(
            dimensionSection?.section_label,
            'Selected Dimensions',
        ),
        selectedKpisPanelTitle: toSelectedPanelTitle(
            kpiSection?.section_label,
            'Selected User Inputs/KPIs',
        ),
        dimensionFields,
        kpiFields,
        initialDimensionSelections: buildEmptySelections(dimensionFields),
        initialKpiSelections: buildEmptySelections(kpiFields),
    };
};

export const EMPTY_CUSTOM_LENS_PARSED_CONFIG: CustomLensParsedConfig = {
    featureName: '',
    lensNameMaxLength: 20,
    lensNamePlaceholder: '',
    showIncludeAiSuggestions: false,
    includeAiSuggestionsDefault: false,
    includeAiSuggestionsDisabled: false,
    includeAiSuggestionsLabel: '',
    dimensionSectionLabel: '',
    kpiSectionLabel: '',
    selectedDimensionsPanelTitle: 'Selected Dimensions',
    selectedKpisPanelTitle: 'Selected User Inputs/KPIs',
    dimensionFields: [],
    kpiFields: [],
    initialDimensionSelections: {},
    initialKpiSelections: {},
};

export const resetFormState = (parsedConfig: CustomLensParsedConfig) => ({
    lensName: '',
    includeAiSuggestions: parsedConfig.includeAiSuggestionsDefault,
    dimensionSelections: { ...parsedConfig.initialDimensionSelections },
    kpiSelections: { ...parsedConfig.initialKpiSelections },
    selectedDimensionOrder: [] as string[],
    selectedKpiOrder: [] as string[],
});

export const applyLensDataToForm = (data: CustomLensFormData, parsedConfig: CustomLensParsedConfig) => ({
    lensName: data.name,
    includeAiSuggestions: data.includeAiSuggestions,
    dimensionSelections: {
        ...parsedConfig.initialDimensionSelections,
        ...data.dimensionSelections,
    },
    kpiSelections: {
        ...parsedConfig.initialKpiSelections,
        ...data.kpiSelections,
    },
    selectedDimensionOrder: [...data.selectedDimensionOrder],
    selectedKpiOrder: [...data.selectedKpiOrder],
    updatedAt: data.updatedAt ?? null,
});

export const buildDeleteCustomLensRequest = (lensId: string | number) => ({
    lens_id: Number(lensId),
});

const buildOptionLookup = <T extends { label: string }>(options: T[]) =>
    new Map(options.map((option) => [option.label, option]));

const buildFieldLookupByLabel = (fields: SelectionFieldConfig[]) => {
    const fieldByLabel = new Map<string, SelectionFieldConfig>();
    fields.forEach((field) => {
        field.optionMeta.forEach((option) => {
            fieldByLabel.set(option.label, field);
        });
    });
    return fieldByLabel;
};

const buildAttributesFromSelection = (
    fields: SelectionFieldConfig[],
    selectedOrder: string[],
    uiRole: 'DIMENSION' | 'KPI',
    displayOrderOffset = 0,
): CustomLensAttributePayload[] => {
    const optionLookup = buildOptionLookup(fields.flatMap((field) => field.optionMeta));
    const fieldByLabel = buildFieldLookupByLabel(fields);

    return selectedOrder.flatMap((label, index) => {
        const option = optionLookup.get(label);
        const field = fieldByLabel.get(label);
        if (!option?.attribute_key || !field) {
            return [];
        }

        return [
            {
                attribute_key: option.attribute_key,
                ui_role: uiRole,
                section_key: field.sectionKey,
                group_key: field.groupKey,
                display_order: displayOrderOffset + index + 1,
            },
        ];
    });
};

export const normalizeUpsertCustomLensResponse = (data: unknown): UpsertCustomLensResponse | null => {
    if (!data || typeof data !== 'object') {
        return null;
    }

    const record = data as Record<string, unknown>;
    const nestedLens =
        record.lens && typeof record.lens === 'object'
            ? (record.lens as Record<string, unknown>)
            : null;
    const lensId = Number(record.lens_id ?? nestedLens?.lens_id ?? record.id);
    const lensName = String(record.lens_name ?? nestedLens?.lens_name ?? '').trim();

    if (!Number.isFinite(lensId) || !lensName) {
        return null;
    }

    return {
        lens_id: lensId,
        lens_name: lensName,
        is_private: true,
    };
};

export const buildCreateCustomLensRequest = (
    formData: CustomLensFormData,
    parsedConfig: CustomLensParsedConfig,
): CreateCustomLensRequest => {
    const dimensionAttributes = buildAttributesFromSelection(
        parsedConfig.dimensionFields,
        formData.selectedDimensionOrder,
        'DIMENSION',
    );

    const kpiAttributes = buildAttributesFromSelection(
        parsedConfig.kpiFields,
        formData.selectedKpiOrder,
        'KPI',
        dimensionAttributes.length,
    );

    return {
        lens_name: formData.name,
        attributes: [...dimensionAttributes, ...kpiAttributes],
        system_columns: formData.includeAiSuggestions
            ? [{ system_column_key: 'ai_suggestion' }]
            : [],
    };
};

export const buildCustomLensUpdateRequest = (
    formData: CustomLensFormData,
    parsedConfig: CustomLensParsedConfig,
): UpdateLensRequest => {
    const request: UpdateLensRequest = buildCreateCustomLensRequest(formData, parsedConfig);
    const updatedAt = normalizeLensUpdatedAt(formData.updatedAt);

    if (updatedAt) {
        request.updated_at = updatedAt;
    }

    return request;
};

const toFieldKeyFromGroup = (group: LensConfigApiCatalogGroup): string =>
    toFieldKey({
        input_id: group.group_key,
        input_label: group.group_name,
    });

const getSelectedLabelsFromCatalogGroup = (group: LensConfigApiCatalogGroup, uiRole: 'DIMENSION' | 'KPI') =>
    [...group.items]
        .filter((item) => item.is_selected && item.ui_role === uiRole && !item.is_disabled)
        .sort((left, right) => (left.display_order ?? 0) - (right.display_order ?? 0))
        .map((item) => item.label);

const applySelectedItemsToFormSelections = (
    selectedItems: LensConfigApiSelectedItem[],
    parsedConfig: CustomLensParsedConfig,
    dimensionSelections: Record<string, string[]>,
    kpiSelections: Record<string, string[]>,
) => {
    selectedItems.forEach((item) => {
        if (item.item_type !== 'ATTRIBUTE') {
            return;
        }

        const label = item.label;
        if (item.ui_role === 'DIMENSION') {
            const field = findFieldForSelectedItem(parsedConfig.dimensionFields, item);
            if (!field) {
                return;
            }

            if (!dimensionSelections[field.key].includes(label)) {
                dimensionSelections[field.key] = [...dimensionSelections[field.key], label];
            }
            return;
        }

        if (item.ui_role === 'KPI') {
            const field = findFieldForSelectedItem(parsedConfig.kpiFields, item);
            if (!field) {
                return;
            }

            if (!kpiSelections[field.key].includes(label)) {
                kpiSelections[field.key] = [...kpiSelections[field.key], label];
            }
        }
    });
};

const findFieldForSelectedItem = (fields: SelectionFieldConfig[], item: LensConfigApiSelectedItem) => {
    if (!item.attribute_key) {
        return fields.find((field) => field.optionMeta.some((option) => option.label === item.label));
    }

    return (
        fields.find((field) =>
            field.optionMeta.some(
                (option) => option.attribute_key === item.attribute_key || option.id === item.attribute_key,
            ),
        ) ??
        fields.find((field) => field.optionMeta.some((option) => option.label === item.label))
    );
};

export const parseCustomLensEditFormFromApi = (
    data: unknown,
    parsedConfig: CustomLensParsedConfig,
): CustomLensFormData | null => {
    const payload = data as LensConfigApiResponse;
    const lensId = payload.lens?.lens_id;
    const lensNameInput = payload.view_config?.inputs?.find((input) => input.input_id === 'lens_name');
    const lensName = payload.lens?.lens_name ?? lensNameInput?.value ?? '';

    if (lensId == null || !lensName) {
        return null;
    }

    const selectedItems = sortSelectedItemsByDisplayOrder(payload.selected_items ?? []);
    const dimensionSelections = { ...parsedConfig.initialDimensionSelections };
    const kpiSelections = { ...parsedConfig.initialKpiSelections };

    payload.catalog?.sections?.forEach((section) => {
        if (section.section_key === 'dimensions') {
            section.groups.forEach((group) => {
                dimensionSelections[toFieldKeyFromGroup(group)] = getSelectedLabelsFromCatalogGroup(
                    group,
                    'DIMENSION',
                );
            });
            return;
        }

        if (section.section_key === 'kpis') {
            section.groups.forEach((group) => {
                kpiSelections[toFieldKeyFromGroup(group)] = getSelectedLabelsFromCatalogGroup(group, 'KPI');
            });
        }
    });

    applySelectedItemsToFormSelections(selectedItems, parsedConfig, dimensionSelections, kpiSelections);

    const selectedDimensionOrder = selectedItems
        .filter((item) => item.item_type === 'ATTRIBUTE' && item.ui_role === 'DIMENSION')
        .map((item) => item.label);
    const selectedKpiOrder = selectedItems
        .filter((item) => item.item_type === 'ATTRIBUTE' && item.ui_role === 'KPI')
        .map((item) => item.label);

    const fallbackDimensionOrder = Object.values(dimensionSelections).flat();
    const fallbackKpiOrder = Object.values(kpiSelections).flat();

    const includeAiSuggestions = parseIncludeAiSuggestions(payload, selectedItems);

    return {
        id: lensId,
        name: String(lensName),
        updatedAt: getLensUpdatedAtFromApi(payload),
        includeAiSuggestions,
        dimensionSelections,
        kpiSelections,
        selectedDimensionOrder: selectedDimensionOrder.length > 0 ? selectedDimensionOrder : fallbackDimensionOrder,
        selectedKpiOrder: selectedKpiOrder.length > 0 ? selectedKpiOrder : fallbackKpiOrder,
    };
};

const getLensNameBlockingError = (
    name: string,
    existingNames: string[],
    excludeName?: string,
): string | null => {
    if (!name) return null;

    if (!LENS_NAME_PATTERN.test(name)) {
        return LENS_NAME_VALIDATION_MESSAGES.SPECIAL_CHARS;
    }

    const trimmed = name.trim();
    const exclude = excludeName?.trim().toLowerCase() ?? '';
    const isDuplicate = existingNames.some(
        (existing) =>
            existing.trim().toLowerCase() === trimmed.toLowerCase() &&
            existing.trim().toLowerCase() !== exclude,
    );

    if (trimmed && isDuplicate) {
        return LENS_NAME_VALIDATION_MESSAGES.DUPLICATE;
    }

    return null;
};

const getLensNameValidationMessage = (
    name: string,
    existingNames: string[],
    lensNameMaxLength: number,
    excludeName?: string,
): string | null => {
    const blockingError = getLensNameBlockingError(name, existingNames, excludeName);
    if (blockingError) return blockingError;

    if (name.length >= lensNameMaxLength) {
        return LENS_NAME_VALIDATION_MESSAGES.MAX_LENGTH;
    }

    return null;
};

const getDimensionKpiConflicts = (dimensions: string[], kpis: string[]): string[] => {
    const kpiSet = new Set(kpis);
    return dimensions.filter((dimension) => kpiSet.has(dimension));
};

const getCreateLensButtonTooltip = (
    isEdit: boolean,
    hasMinDimensions: boolean,
    hasMinKpis: boolean,
): string | null => {
    if (isEdit) return null;

    const missingDimensions = !hasMinDimensions;
    const missingKpis = !hasMinKpis;

    if (missingDimensions && missingKpis) return null;
    if (missingDimensions) return CREATE_LENS_TOOLTIP_MESSAGES.ADD_DIMENSION;
    if (missingKpis) return CREATE_LENS_TOOLTIP_MESSAGES.ADD_KPI;

    return null;
};

const hasDisabledItemsInField = (fieldOptions: string[], disabledOptions: string[]): boolean => {
    const disabledSet = new Set(disabledOptions);
    return fieldOptions.some((option) => disabledSet.has(option));
};

export const shouldShowDisabledItemsMessage = (
    message: string | undefined,
    fieldOptions: string[],
    disabledOptions: string[],
): boolean => Boolean(message) && hasDisabledItemsInField(fieldOptions, disabledOptions);

export const getVisibleSelectionFields = (
    fields: SelectionFieldConfig[],
    isEdit: boolean,
    selections: Record<string, string[]> = {},
): SelectionFieldConfig[] => {
    if (isEdit) {
        return fields.filter(
            (field) => field.options.length > 0 || (selections[field.key]?.length ?? 0) > 0,
        );
    }

    return fields.filter((field) => field.options.length > 0);
};

export const getCustomLensFormValidation = ({
    isEdit,
    lensName,
    lensNameTouched,
    lensNameMaxLength,
    existingLensNames,
    originalLensName,
    selectedDimensionOrder,
    selectedKpiOrder,
}: CustomLensFormValidationParams): CustomLensFormValidation => {
    const trimmedLensName = lensName.trim();
    const lensNameBlockingError = getLensNameBlockingError(lensName, existingLensNames, originalLensName);
    const lensNameError = getLensNameValidationMessage(
        lensName,
        existingLensNames,
        lensNameMaxLength,
        originalLensName,
    );
    const hasMinDimensions = selectedDimensionOrder.length > 0;
    const hasMinKpis = selectedKpiOrder.length > 0;
    const hasDimensionKpiConflicts = getDimensionKpiConflicts(selectedDimensionOrder, selectedKpiOrder).length > 0;
    const hasSelections = hasMinDimensions || hasMinKpis;
    const isNameValid = trimmedLensName.length > 0 && !lensNameBlockingError;
    const canSave = isNameValid && hasMinDimensions && hasMinKpis && !hasDimensionKpiConflicts;

    return {
        lensNameError,
        hasSelections,
        canSave,
        showLensNameError: lensNameTouched && Boolean(lensNameError),
        showLensNameBlockingError: lensNameTouched && Boolean(lensNameBlockingError),
        createButtonTooltip: getCreateLensButtonTooltip(isEdit, hasMinDimensions, hasMinKpis),
    };
};

export const filterConflictingSelections = (selected: string[], conflicts: string[]): string[] => {
    const conflictSet = new Set(conflicts);
    return selected.filter((item) => !conflictSet.has(item));
};

/**
 * Keeps a consistent ordered list of selected items across categories.
 * - Items removed from selections are dropped from the order.
 * - Newly added items are appended at the end.
 */
export const syncOrderedSelection = (
    prevOrder: string[],
    categorySelections: Record<string, string[]>,
): string[] => {
    const allSelected = Object.values(categorySelections).flat();
    const selectedSet = new Set(allSelected);
    const kept = prevOrder.filter((item) => selectedSet.has(item));
    const keptSet = new Set(kept);
    const added = allSelected.filter((item) => !keptSet.has(item));
    return [...kept, ...added];
};

/**
 * Removes a single item from whichever category it belongs to.
 * Returns the original selections object unchanged if the item is not found.
 */
export const removeFromCategorySelections = (
    selections: Record<string, string[]>,
    item: string,
): Record<string, string[]> => {
    const categoryKey = Object.entries(selections).find(([, opts]) => opts.includes(item))?.[0];
    if (!categoryKey) return selections;
    return {
        ...selections,
        [categoryKey]: selections[categoryKey].filter((option) => option !== item),
    };
};

export const parseSystemLensEditFormFromApi = (data: unknown): SystemLensEditFormData | null => {
    const payload = data as LensConfigApiResponse;
    const lensId = payload.lens?.lens_id;
    const lensName = payload.lens?.lens_name;

    if (lensId == null || !lensName) {
        return null;
    }

    const selectedItems = sortSelectedItemsByDisplayOrder(payload.selected_items ?? []);
    const dimensionOrder = selectedItems
        .filter((item) => item.item_type === 'ATTRIBUTE' && item.ui_role === 'DIMENSION')
        .map((item) => item.label);
    const kpiOrder = selectedItems
        .filter((item) => item.item_type === 'ATTRIBUTE' && item.ui_role === 'KPI')
        .map((item) => item.label);
    const includeAiSuggestions = parseIncludeAiSuggestions(payload, selectedItems);

    return {
        lensId,
        lensName,
        updatedAt: getLensUpdatedAtFromApi(payload),
        dimensionOrder,
        kpiOrder,
        includeAiSuggestions,
    };
};

const resolveCatalogGroupKey = (
    catalogSections: LensConfigApiCatalogSection[],
    attributeKey: string,
    uiRole: 'DIMENSION' | 'KPI',
): string | undefined => {
    const sectionKey = uiRole === 'DIMENSION' ? 'dimensions' : 'kpis';

    for (const section of catalogSections) {
        if (section.section_key !== sectionKey) {
            continue;
        }

        for (const group of section.groups) {
            for (const catalogItem of group.items) {
                if (catalogItem.attribute_key === attributeKey && catalogItem.ui_role === uiRole) {
                    return group.group_key;
                }
            }
        }
    }

    return undefined;
};

export const buildSystemLensUpdateRequest = (
    formData: SystemLensEditFormData,
    selectedItems: LensConfigApiSelectedItem[],
    catalogSections: LensConfigApiCatalogSection[] = [],
    apiResponse?: LensConfigApiResponse,
): UpdateLensRequest => {
    const attributeItems = selectedItems.filter((item) => item.item_type === 'ATTRIBUTE' && item.attribute_key);
    const attributeByLabel = new Map(attributeItems.map((item) => [item.label, item]));

    const buildAttributes = (labels: string[], uiRole: 'DIMENSION' | 'KPI', displayOrderOffset = 0) =>
        labels.flatMap((label, index) => {
            const item = attributeByLabel.get(label);
            if (!item?.attribute_key) {
                return [];
            }

            const groupKey =
                resolveCatalogGroupKey(catalogSections, item.attribute_key, uiRole) ?? item.attribute_key;

            return [
                {
                    attribute_key: item.attribute_key,
                    ui_role: uiRole,
                    section_key: uiRole === 'DIMENSION' ? 'dimensions' : 'kpis',
                    group_key: groupKey,
                    display_order: displayOrderOffset + index + 1,
                } satisfies CustomLensAttributePayload,
            ];
        });

    const dimensionAttributes = buildAttributes(formData.dimensionOrder, 'DIMENSION');
    const kpiAttributes = buildAttributes(formData.kpiOrder, 'KPI', dimensionAttributes.length);

    const request: UpdateLensRequest = {
        lens_name: formData.lensName,
        attributes: [...dimensionAttributes, ...kpiAttributes],
        system_columns: formData.includeAiSuggestions ? [{ system_column_key: 'ai_suggestion' }] : [],
    };

    const updatedAt =
        normalizeLensUpdatedAt(formData.updatedAt) ?? getLensUpdatedAtFromApi(apiResponse);
    if (updatedAt) {
        request.updated_at = updatedAt;
    }

    return request;
};

const normalizeLensUpdatedAt = (value: unknown): string | undefined => {
    if (typeof value === 'string' && value.trim()) {
        return value.trim();
    }

    return undefined;
};

const getLensUpdatedAtFromApi = (data: unknown): string | undefined => {
    if (data == null) {
        return undefined;
    }

    const payload = data as LensConfigApiResponse;

    return (
        normalizeLensUpdatedAt(payload.updated_at) ?? normalizeLensUpdatedAt(payload.lens?.updated_at)
    );
};

const fetchLensUpdatedAtFromList = async (lensId: number): Promise<string | undefined> => {
    try {
        const response = await axios.get<PPALensListResponse>(ppaUrls.getLensListUrl);
        const lens = response.data.lenses?.find((item) => item.lens_id === lensId);

        return normalizeLensUpdatedAt(lens?.updated_at);
    } catch {
        return undefined;
    }
};

export const enrichLensConfigWithUpdatedAt = async (
    raw: unknown,
    lensId: number,
): Promise<LensConfigApiResponse> => {
    const payload = raw as LensConfigApiResponse;
    const existingUpdatedAt = getLensUpdatedAtFromApi(payload);

    if (existingUpdatedAt) {
        return payload;
    }

    const updatedAt = await fetchLensUpdatedAtFromList(lensId);
    if (!updatedAt) {
        return payload;
    }

    return {
        ...payload,
        updated_at: updatedAt,
        lens: payload.lens ? { ...payload.lens, updated_at: updatedAt } : payload.lens,
    };
};

export const resolveLensUpdateRequest = (
    _lensId: number,
    payload: UpdateLensRequest,
): UpdateLensRequest => {
    const updatedAt = normalizeLensUpdatedAt(payload.updated_at);

    if (!updatedAt) {
        return payload;
    }

    return {
        ...payload,
        updated_at: updatedAt,
    };
};

export const getLensCanEditFromApi = (data: unknown): boolean => {
    if (data == null || typeof data !== 'object') {
        return true;
    }

    const payload = data as LensConfigApiResponse;
    return payload.lens?.can_edit ?? true;
};

export const getLensCanModifyCatalogFromApi = (data: unknown): boolean => {
    if (data == null || typeof data !== 'object') {
        return true;
    }

    const payload = data as LensConfigApiResponse;
    return payload.lens?.can_modify_catalog ?? true;
};

export const getCustomLensLimitFromApi = (data: unknown): number | undefined => {
    if (data == null || typeof data !== 'object') {
        return undefined;
    }

    const payload = data as LensConfigApiResponse;
    const limit = payload.view_config?.limit_per_user;

    return typeof limit === 'number' && limit > 0 ? limit : undefined;
};
